import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Globe, Lock, Share2, Users } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";

export const AccessBadge = ({ item }) => {
  if (item.shared && !item.owned) return <span className="inline-flex items-center gap-1 text-xs rounded-full bg-[#3B82F6]/20 text-[#93C5FD] px-2 py-0.5" data-testid="badge-shared-with-me"><Users className="h-3 w-3" />Seninle paylaşıldı</span>;
  if (item.visibility === "private") {
    return (
      <span className="inline-flex items-center gap-1 text-xs rounded-full bg-slate-700/60 text-slate-200 px-2 py-0.5" data-testid="badge-private">
        <Lock className="h-3 w-3" />Özel{item.shared_with?.length ? ` · ${item.shared_with.length} kişi` : ""}
      </span>
    );
  }
  return null;
};

export const ShareButton = ({ item, onClick }) => (item.mine ? (
  <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onClick(item); }} data-testid={`share-${item.id}`}><Share2 className="h-4 w-4 mr-1" />Paylaş</Button>
) : null);

export const ShareDialog = ({ kind, item, onOpenChange, onSaved }) => {
  const [teachers, setTeachers] = useState([]);
  const [isPublic, setIsPublic] = useState(true);
  const [ids, setIds] = useState([]);
  useEffect(() => {
    if (!item) return;
    setIsPublic(item.visibility !== "private");
    setIds(item.shared_with || []);
    api.get("/auth/directory").then((r) => setTeachers(r.data.filter((t) => t.id !== item.owner_id))).catch((e) => toast.error(errMsg(e)));
  }, [item]);
  const toggle = (id) => setIds((xs) => (xs.includes(id) ? xs.filter((x) => x !== id) : [...xs, id]));
  const save = async () => {
    try {
      await api.put(`/share/${kind}/${item.id}`, { visibility: isPublic ? "public" : "private", shared_with: ids });
      toast.success("Paylaşım ayarları kaydedildi");
      onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={Boolean(item)} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="share-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Paylaşım ayarları</DialogTitle></DialogHeader>
        <label className="flex items-center justify-between gap-3 rounded-xl border border-white/10 px-4 py-3 cursor-pointer">
          <span className="flex items-center gap-2 font-semibold">{isPublic ? <Globe className="h-4 w-4 text-[#4ADE80]" /> : <Lock className="h-4 w-4 text-slate-300" />}{isPublic ? "Herkese açık" : "Özel"}</span>
          <Switch checked={isPublic} onCheckedChange={setIsPublic} data-testid="share-public-switch" />
        </label>
        <p className="text-sm text-slate-400">{isPublic ? "Tüm öğretmenler görebilir, kullanabilir ve çoğaltabilir." : "Yalnızca sen ve aşağıda seçtiğin öğretmenler görür."} Paylaşılan öğretmenler düzenleyemez.</p>
        {!isPublic && (
          <div className="max-h-64 overflow-y-auto space-y-1" data-testid="share-teacher-list">
            {teachers.length === 0 && <p className="text-sm text-slate-500">Başka öğretmen yok.</p>}
            {teachers.map((t) => (
              <label key={t.id} className="flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-white/5 cursor-pointer">
                <Checkbox checked={ids.includes(t.id)} onCheckedChange={() => toggle(t.id)} data-testid={`share-teacher-${t.id}`} />
                <span>{t.name}</span>
              </label>
            ))}
          </div>
        )}
        <DialogFooter><Button onClick={save} className="font-bold" data-testid="share-save">Kaydet</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
